import { useCallback, useEffect, useState } from "react";

import { useIsMobile } from "@/shared/hooks/use-mobile";

import {
  browserStorage,
  isPwaInstallSnoozed,
  readPwaInstallEnvironment,
  resolvePwaInstallGuide,
  snoozePwaInstall,
} from "./pwaInstallGuide";
import { usePwaInstall } from "./usePwaInstall";

const SHOW_AFTER_MS = 2500;

export function usePwaInstallBanner(enabled: boolean) {
  const { canInstall, install } = usePwaInstall();
  const isMobile = useIsMobile();
  const [environment] = useState(readPwaInstallEnvironment);
  const [snoozed, setSnoozed] = useState(() => isPwaInstallSnoozed(browserStorage(), Date.now()));
  const [settled, setSettled] = useState(false);

  const guide = resolvePwaInstallGuide(environment, canInstall);
  const eligible = enabled && isMobile && !snoozed && guide !== null;

  useEffect(() => {
    if (!eligible || settled) return;
    const timer = window.setTimeout(() => setSettled(true), SHOW_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, [eligible, settled]);

  const dismiss = useCallback(() => {
    snoozePwaInstall(browserStorage(), Date.now());
    setSnoozed(true);
  }, []);

  const installApp = useCallback(async () => {
    const outcome = await install();
    if (outcome === "dismissed") dismiss();
  }, [dismiss, install]);

  return { guide: eligible && settled ? guide : null, dismiss, install: installApp };
}
